import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
const toast = { success: (m) => console.log("[toast]", m), error: (m) => console.error("[toast]", m), info: (m) => console.log("[toast]", m) };
import PageContainer, { PageHeader } from "@/components/PageContainer";
import api from "@/lib/assessments";
import { readAll } from "@/lib/assessments";
const formatApiError = (e) => (e && e.message) || "Unknown error";
import { CheckCircle2, Save, ChevronLeft } from "lucide-react";

/**
 * Generic Likert instrument runner used by PHQ-9 and GAD-7.
 * Props:
 *  - kind: "phq9" | "gad7"
 *  - title, overline, subtitle, items[], scale[{value,label}]
 *  - interpret(total): {band, detail}
 */
export default function InstrumentRunner({ kind, title, overline, subtitle, items, scale, interpret, testidPrefix }) {
  const navigate = useNavigate();
  const [patient, setPatient] = useState("");
  const [notes, setNotes] = useState("");
  const [values, setValues] = useState(Array(items.length).fill(null));
  const [saving, setSaving] = useState(false);

  const total = values.reduce((a, b) => (typeof b === "number" ? a + b : a), 0);
  const complete = values.every((v) => v !== null);
  const interp = complete ? interpret(total) : null;

  const save = async () => {
    if (!complete) {
      toast.error("Please answer every item before saving.");
      return;
    }
    setSaving(true);
    try {
      const responses = {};
      items.forEach((_, i) => (responses[`q${i + 1}`] = values[i]));
      await api.post("/assessments", {
        kind,
        patient_identifier: patient || null,
        responses,
        score: total,
        interpretation: interp.band,
        notes: notes || null,
      });
      toast.success(`${title} saved to history.`);
      navigate("/portal/continuum/tools/history");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageContainer testid={`${testidPrefix}-page`}>
      <Link
        to="/portal/continuum/tools"
        className="text-sm text-ink-muted hover:text-navy inline-flex items-center gap-1 mb-4"
        data-testid={`${testidPrefix}-back`}
      >
        <ChevronLeft size={14} strokeWidth={2} /> All tools
      </Link>

      <PageHeader overline={overline} title={title} subtitle={subtitle} />

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <div className="card p-5">
            <label className="label">Patient identifier (initials or MRN)</label>
            <input
              className="field"
              value={patient}
              onChange={(e) => setPatient(e.target.value)}
              placeholder="e.g. J.D. or MRN-1042"
              data-testid={`${testidPrefix}-patient`}
            />
            <div className="text-xs text-ink-muted mt-2">
              Use a non-identifying reference. This value is stored on your account only.
            </div>
          </div>

          <div className="card p-5">
            <div className="overline mb-2">Over the last two weeks, how often have you been bothered by:</div>
            <ol className="space-y-4 mt-3">
              {items.map((item, i) => (
                <li key={i} className="pb-4 border-b border-line-soft last:border-0 last:pb-0" data-testid={`${testidPrefix}-item-${i + 1}`}>
                  <div className="text-sm text-navy mb-3">
                    <span className="text-ink-muted mr-2">{i + 1}.</span>
                    {item}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {scale.map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => {
                          const next = [...values];
                          next[i] = opt.value;
                          setValues(next);
                        }}
                        className={`btn text-sm ${values[i] === opt.value ? "btn-primary" : "btn-secondary"}`}
                        data-testid={`${testidPrefix}-item-${i + 1}-opt-${opt.value}`}
                      >
                        <span className="text-xs text-ink-faint mr-1">{opt.value}</span>
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </li>
              ))}
            </ol>
          </div>

          <div className="card p-5">
            <label className="label">Clinician notes (optional)</label>
            <textarea
              className="field min-h-[80px]"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              data-testid={`${testidPrefix}-notes`}
              placeholder="Context, safety plan, next steps."
            />
          </div>
        </div>

        <aside className="lg:sticky lg:top-6 self-start">
          <div className="card p-6" data-testid={`${testidPrefix}-summary`}>
            <div className="overline mb-2">Live score</div>
            <div className="text-5xl font-semibold text-navy">
              {total}
              <span className="text-xl text-ink-muted"> / {items.length * (scale.length - 1)}</span>
            </div>

            {interp ? (
              <div className="mt-4 fade-up">
                <div className="badge badge-teal mb-2">{interp.band}</div>
                <p className="text-sm text-ink leading-relaxed">{interp.detail}</p>
              </div>
            ) : (
              <div className="mt-4 text-sm text-ink-muted">
                Complete every item to see the interpretation band.
              </div>
            )}

            <div className="rule my-4" />

            <button
              onClick={save}
              disabled={!complete || saving}
              className="btn btn-primary w-full justify-center"
              data-testid={`${testidPrefix}-save`}
            >
              {saving ? "Saving..." : (
                <>
                  <Save size={14} strokeWidth={2} /> Save to history
                </>
              )}
            </button>
            {!complete && (
              <div className="text-xs text-ink-muted mt-2 text-center">
                {values.filter((v) => v === null).length} item{values.filter((v) => v === null).length === 1 ? "" : "s"} remaining
              </div>
            )}
          </div>
        </aside>
      </div>
    </PageContainer>
  );
}
